import React from "react";
import { Tag, Input, Button, Space, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { formatCurrencyWithConversion } from "../../../../utils/currencyUtils";

const { Text } = Typography;

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
const defaultParseNumber = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "")
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Invoice columns configuration for the CashAllocation component
 * @param {Object} props - Properties needed for invoice columns
 * @param {Function} props.handleCashChange - Function to handle cash amount changes
 * @param {Function} props.handleAllocateCash - Function to handle cash allocation
 * @param {Boolean} props.loadingAllocation - Loading state for allocation actions
 * @param {Function} props.parseNumberFn - Optional custom number parser function
 * @returns {Array} Array of column configurations for the invoice table
 */
const getInvoiceColumns = ({
  handleCashChange,
  handleAllocateCash,
  loadingAllocation,
  parseNumberFn = defaultParseNumber,
  onViewPartner,
}) => [
  {
    title: "Invoice #",
    dataIndex: "number",
    key: "number",
    width: 100,
    ellipsis: true,
    sorter: (a, b) => (a.number || "").localeCompare(b.number || ""),
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input placeholder="Search invoice #" value={selectedKeys[0]} onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])} onPressEnter={confirm} style={{ marginBottom: 8, display: 'block' }} />
        <Space><Button type="primary" onClick={confirm} size="small">Search</Button><Button onClick={clearFilters} size="small">Reset</Button></Space>
      </div>
    ),
    onFilter: (value, record) => (record.number || "").toLowerCase().includes(value.toLowerCase()),
  },
  {
    title: "Partner",
    dataIndex: "partner_name",
    key: "partner_name",
    width: 120,
    ellipsis: true,
    render: (partnerName, record) => {
      // Get partner name from various possible fields
      const name = partnerName || record?.partner?.name || "N/A";
      // Get partner ID from various possible fields
      const partnerId = record?.partner_id || record?.partner?.id || (typeof record?.partner === "number" ? record?.partner : null);
      if (partnerId && onViewPartner) {
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewPartner(partnerId);
            }}
            style={{ color: "#1890ff", cursor: "pointer" }}
            title="View partner transactions"
          >
            {name}
          </a>
        );
      }
      return name;
    },
  },
  {
    title: "Issue Date",
    dataIndex: "issue_date",
    key: "issue_date",
    width: 100,
    render: (date) => (date ? dayjs(date).format("MM/DD/YY") : ""),
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    width: 90,
    render: (date) => (date ? dayjs(date).format("MM/DD/YY") : ""),
  },
  {
    title: "Terms",
    dataIndex: "payment_terms_code",
    key: "payment_terms_code",
    width: 70,
    ellipsis: true,
    render: (terms) => (terms ? terms : "N/A"),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 80,
    render: (status, record) => {
      let color = "default";
      if (status === "paid") color = "success";
      if (status === "partial") color = "warning";
      if (status === "overdue") color = "error";
      if (status === "draft") color = "blue";
      return (
        <Tag color={color} style={{ fontSize: 11 }}>
          {record.status_display || (status ? status.toUpperCase() : "")}
        </Tag>
      );
    },
  },
  {
    title: "Total",
    dataIndex: "total_amount",
    key: "total_amount",
    width: 100,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Paid",
    dataIndex: "paid_amount",
    key: "paid_amount",
    width: 100,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Remaining",
    dataIndex: "remainingAmount",
    key: "remaining",
    width: 100,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Eligible",
    key: "eligible",
    width: 65,
    render: (record) => {
      const remaining = parseFloat(record.remainingAmount);
      return remaining > 0 ? (
        <Tag color="green">Yes</Tag>
      ) : (
        <Tag color="red">No</Tag>
      );
    },
  },
  {
    title: "Allocated",
    dataIndex: "current_allocation_amount",
    key: "current_allocation",
    width: 95,
    render: (amount, record) => {
      const currency =
        record?.currency || record?.originalData?.currency || "$";
      return formatCurrencyWithConversion(amount || 0, currency);
    },
  },
  {
    title: "Cash Amt",
    key: "cash",
    width: 110,
    render: (record) => {
      // Show current allocation amount if it exists
      const hasExistingAllocation = !!record.current_allocation_id;

      if (hasExistingAllocation) {
        return (
          <Input
            type="text"
            size="small"
            style={{ width: 100 }}
            value={record.cashAmount}
            onChange={(e) => handleCashChange(record.id, e)}
            disabled={loadingAllocation}
            placeholder="Enter amount"
            status="success"
          />
        );
      }

      // Only allow cash for invoices with remaining balance
      const remaining = parseFloat(record.remainingAmount);
      return remaining > 0 ? (
        <Input
          type="text"
          size="small"
          style={{ width: 100 }}
          value={record.cashAmount}
          onChange={(e) => handleCashChange(record.id, e)}
          disabled={loadingAllocation || record.status === "paid"}
          placeholder="Enter amount"
        />
      ) : (
        <Text type="secondary">N/A</Text>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    width: 160,
    render: (_, record) => {
      // Always get the allocation ID if it exists
      const allocationId = record.current_allocation_id;

      return (
        <Space>
          {/* Allocate button - enabled when there's a valid cash amount > 0 */}
          <Button
            type="primary"
            size="small"
            onClick={() => {
              // Convert to number only when submitting (handle commas)
              // IMPORTANT: Always use defaultParseNumber to handle comma-formatted numbers like "2,007.430"
              // parseFloat("2,007.430") incorrectly returns 2, but defaultParseNumber returns 2007.43
              const amount = record.cashAmount
                ? defaultParseNumber(record.cashAmount)
                : 0;
              if (amount > 0) {
                handleAllocateCash(
                  record.id,
                  amount,
                  record.is_allocated ? allocationId : null
                );
              }
            }}
            disabled={
              loadingAllocation ||
              // Check approval status - only allow allocation if approved
              (record.approval_status && record.approval_status !== 'approved') ||
              record.status === "paid" ||
              !record.cashAmount ||
              defaultParseNumber(record.cashAmount) <= 0
            }
            title={
              record.approval_status && record.approval_status !== 'approved'
                ? `Cannot allocate: Invoice must be approved (Current status: ${record.approval_status})`
                : undefined
            }
          >
            {record.is_allocated ? "Update" : "Allocate"}
          </Button>

          {/* Unallocate button - enabled when allocation exists */}
          <Button
            danger
            size="small"
            onClick={() => {
              handleAllocateCash(record.id, 0, allocationId);
            }}
            disabled={!record.is_allocated || loadingAllocation}
            title={
              !record.is_allocated
                ? "No allocation to remove"
                : "Remove allocation"
            }
          >
            Unallocate
          </Button>
        </Space>
      );
    },
  },
];

export default getInvoiceColumns;
